import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { semesterReviewService, authService } from '../../services';
import semesterNoteService from '../../services/semesterNoteService';
import { useLanguage } from '../../contexts/LanguageContext';
import SemesterReviewForm from './SemesterReviewForm';
import { LeistungsbeurteilungForm } from './LeistungsbeurteilungForm';
import { useAutoSave } from '../../hooks/useAutoSave';
import LeaveProtectionModal from '../common/LeaveProtectionModal';

const SemesterReviewDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [review, setReview] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [currentAnswers, setCurrentAnswers] = useState(null);
  const [showWorkflow, setShowWorkflow] = useState(false);

  // Placeholder State
  const [previousReviews, setPreviousReviews] = useState([]);
  const [selectedPlaceholderSource, setSelectedPlaceholderSource] = useState('');
  const [placeholders, setPlaceholders] = useState({});
  // Prefill mode: OFF by default (placeholder-only). Turning it on does not itself copy
  // anything - "Apply comments" is a separate, explicit action so real content can never be
  // overwritten just by toggling a checkbox or changing the source.
  const [prefillEnabled, setPrefillEnabled] = useState(false);
  const [applyingComments, setApplyingComments] = useState(false);
  // Coarse role category of the signed-in user ("TRAINER" | "PRACTICE_INSTRUCTOR" | "ADMIN" |
  // "LEARNER"), mirroring the backend's PreviousReportDTO.authorRole values - used only to
  // label a comment source as "Own comments" vs. by its actual author role.
  const [currentUserRoleCategory, setCurrentUserRoleCategory] = useState(null);

  // Smart Goal Placeholder State (for Leistungsbeurteilung)
  const [previousMeetings, setPreviousMeetings] = useState([]);
  const [selectedMeetingSource, setSelectedMeetingSource] = useState('');
  const [smartGoalPlaceholder, setSmartGoalPlaceholder] = useState('');

  // Validation Modal State
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');

  // Status change confirmation modal ("IN_REVIEW" | "COMPLETED" | null)
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  // True for Berufsbildner/Praxisbildner/Admin, false for the apprentice
  const [isTrainerView, setIsTrainerView] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [pdfPreviewFilename, setPdfPreviewFilename] = useState('Semesterreview.pdf');

  // Semester quick notes (read-only context in semester review)
  const [reviewNotes, setReviewNotes] = useState([]);
  const [reviewNotesLoading, setReviewNotesLoading] = useState(false);
  const [reviewNotesError, setReviewNotesError] = useState(null);
  const [canViewNotes, setCanViewNotes] = useState(false);

  const [toast, setToast] = useState({
    show: false,
    message: '',
    type: 'success',
  });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    // Increase duration for error messages to give user time to read
    const duration = type === 'error' ? 5000 : 3000;
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), duration);
  };

  const handleAutoSave = async (answers) => {
    // Silent save - no toast, no full page reload
    if (!id || !answers) return;

    // Calculate total item count from template for bonus calculation
    let totalCompetenceItems = 0;
    if (template && template.sections) {
      template.sections.forEach((section) => {
        if (section.items) totalCompetenceItems += section.items.length;
      });
    }

    try {
      const result = await semesterReviewService.saveAnswers(id, answers, totalCompetenceItems);

      // Update bonus stats from backend response (Strict Requirements)
      if (result && result.totalPoints !== undefined) {
        setReview((prev) => ({
          ...prev,
          totalPoints: result.totalPoints,
          maxPoints: result.maxPoints,
          bonusPercentage: result.bonusPercentage,
          answers: answers, // Ensure answers are optimistic updated locally too if not already
        }));
      }
    } catch (error) {
      console.error('AutoSave error:', error);
      throw error; // Propagate to hook
    }
  };

  // AutoSave Hook
  const {
    status: autoSaveStatus,
    lastSavedTime,
    isDirty,
    blocker,
    manualSave,
  } = useAutoSave({
    data: currentAnswers || (review ? review.answers : null),
    onSave: handleAutoSave,
    isEditable: isEditable && !!review,
    debounceMs: 2000,
  });

  useEffect(() => {
    if (autoSaveStatus === 'error') {
      showToast('Error auto-saving', 'error');
    }
  }, [autoSaveStatus]);

  useEffect(() => {
    return () => {
      semesterReviewService.revokeObjectUrl(pdfPreviewUrl);
    };
  }, [pdfPreviewUrl]);
  //   type: "success",
  // });

  // const showToast = (message, type = "success") => {
  //   setToast({ show: true, message, type });
  //   setTimeout(
  //     () => setToast({ show: false, message: "", type: "success" }),
  //     3000
  //   );
  // };

  useEffect(() => {
    const initializePage = async () => {
      await loadCurrentUser();
      await loadReview();
    };
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, language]);

  // Load Previous Reviews for Placeholders (Bildungsbericht)
  useEffect(() => {
    if (review && review.learnerId && review.templateCode !== 'BB_LEISTUNGSBEURTEILUNG') {
      semesterReviewService
        .getPreviousReviews(review.learnerId)
        .then((data) => {
          // Exclude current review from possible sources
          const list = data.filter((r) => r.id !== review.id);
          setPreviousReviews(list);

          if (list.length > 0) {
            setSelectedPlaceholderSource(list[0].id);
          }
        })
        .catch((err) => console.error('Failed to load previous reviews', err));
    } else if (review && review.learnerId && review.templateCode === 'BB_LEISTUNGSBEURTEILUNG') {
      // Load Previous Meetings for Smart Goal (Semestergespräch)
      semesterReviewService
        .getPreviousMeetings(review.learnerId, review.id)
        .then((data) => {
          const list = (data || []).filter((r) => Number(r.id) !== Number(review.id));
          setPreviousMeetings(list);

          if (list.length > 0) {
            setSelectedMeetingSource(String(list[0].id));
          } else {
            setSelectedMeetingSource('');
          }
        })
        .catch((err) => console.error('Failed to load previous meetings', err));
    }
  }, [review]);

  // Load Placeholders on selection change (Bildungsbericht)
  useEffect(() => {
    if (selectedPlaceholderSource) {
      semesterReviewService
        .getReviewPlaceholders(selectedPlaceholderSource)
        .then((data) => {
          setPlaceholders(data.placeholders || {});
        })
        .catch((err) => console.error('Failed to load placeholders', err));
    } else {
      setPlaceholders({});
    }
  }, [selectedPlaceholderSource]);

  // Load Smart Goal on selection change (Semestergespräch)
  useEffect(() => {
    if (selectedMeetingSource) {
      semesterReviewService
        .getSmartGoal(selectedMeetingSource)
        .then((data) => {
          setSmartGoalPlaceholder(data.smartGoal || '');
        })
        .catch((err) => console.error('Failed to load smart goal', err));
    } else {
      setSmartGoalPlaceholder('');
    }
  }, [selectedMeetingSource]);

  // Load quick notes for this review's semester.
  // Uses review.semesterId when set; falls back to review.resolvedSemesterId
  // which the backend derives from the learner's apprenticeshipStartDate + review.createdAt.
  useEffect(() => {
    if (!canViewNotes) return;
    const semesterId = review?.semesterId ?? review?.resolvedSemesterId;
    if (!review?.learnerId || !semesterId) return;

    setReviewNotesLoading(true);
    setReviewNotesError(null);

    semesterNoteService
      .getNotes(review.learnerId, semesterId)
      .then((data) => {
        setReviewNotes(data || []);
      })
      .catch((err) => {
        console.warn('Could not load semester notes', err);
        setReviewNotesError('Could not load quick notes.');
      })
      .finally(() => setReviewNotesLoading(false));
  }, [review?.learnerId, review?.semesterId, review?.resolvedSemesterId, canViewNotes]);

  const loadCurrentUser = async () => {
    try {
      const user = authService.getCurrentUser();
      const roleNames = (user?.roles || []).map((r) => {
        const name = typeof r === 'string' ? r : r?.name || r?.authority || '';
        return name.toUpperCase().replace(/^ROLE_/, '');
      });

      const trainerRoles = ['ADMIN', 'BERUFSBILDNER', 'PRAXISBILDNER', 'PRACTICE_INSTRUCTOR'];
      setIsTrainerView(roleNames.some((name) => trainerRoles.includes(name)));

      // Quick Notes are a Berufsbildner-only tool (Admin included since it already has
      // equivalent access everywhere else). This mirrors the backend's @PreAuthorize on
      // GET /semester-notes - Praxisbildner/Practice Instructor and learners must not see them.
      const quickNotesRoles = ['ADMIN', 'BERUFSBILDNER'];
      setCanViewNotes(roleNames.some((name) => quickNotesRoles.includes(name)));

      if (roleNames.includes('ADMIN')) {
        setCurrentUserRoleCategory('ADMIN');
      } else if (roleNames.includes('BERUFSBILDNER')) {
        setCurrentUserRoleCategory('TRAINER');
      } else if (roleNames.includes('PRAXISBILDNER') || roleNames.includes('PRACTICE_INSTRUCTOR')) {
        setCurrentUserRoleCategory('PRACTICE_INSTRUCTOR');
      } else if (roleNames.includes('LERNENDE')) {
        setCurrentUserRoleCategory('LEARNER');
      }
    } catch (error) {
      console.error('Error loading current user:', error);
    }
  };

  const loadReview = async () => {
    try {
      setLoading(true);
      const data = await semesterReviewService.getReviewById(id);
      setReview(data);

      // Get current user for permissions check
      const user = authService.getCurrentUser();

      // Mark as read if learner is viewing
      if (user && data.learnerId === user.id && !data.isReadByLearner) {
        await semesterReviewService.markAsRead(id);
      }

      // Determine if editable (can edit in DRAFT for everyone, IN_REVIEW for non-authors usually, but simplified here)
      // Actually backend enforces:
      // - Author: DRAFT only
      // - Admin/Trainer: DRAFT or IN_REVIEW for answers? (Backend: saveAnswers: Author or Instructor. Status: DRAFT or IN_REVIEW)
      // BUT if status is COMPLETED, it is strictly read-only for everyone regarding answers.
      const isCompleted = data.status === 'COMPLETED';
      const isAuthor = user && data.authorId === user.id;

      // Allow editing if user is authorized AND review is not completed
      // We rely on backend for granular permission, but frontend should block COMPLETED
      let canEdit = isAuthor && (data.status === 'DRAFT' || data.status === 'IN_REVIEW');

      // If user is Admin or Trainer, they might have edit rights in IN_REVIEW too
      const authorities = user?.authorities || [];
      const isTrainerOrAdmin = authorities.some((a) =>
        [
          'ROLE_ADMIN',
          'ROLE_BERUFSBILDNER',
          'ROLE_PRAXISBILDNER',
          'ROLE_PRACTICE_INSTRUCTOR',
        ].includes(a.authority || a)
      );

      if (isTrainerOrAdmin) {
        canEdit = data.status === 'DRAFT' || data.status === 'IN_REVIEW';
      }

      if (isCompleted) {
        canEdit = false;
      }

      setIsEditable(canEdit);

      // Load template from JSON path
      await loadTemplate(data.templateCode);
    } catch (error) {
      console.error('Error loading review:', error);
      alert('Error loading review');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async (templateCode) => {
    try {
      // Map template codes to JSON file paths
      const templatePaths = {
        KOMPETENZEN:
          language === 'en'
            ? '/Semesterreview_Content/semesterreview_kompetenzen_en.json'
            : '/Semesterreview_Content/semesterreview_kompetenzen.json',
        BEWERTUNG_DER_AUSBILDUNG:
          language === 'en'
            ? '/Semesterreview_Content/semesterreview_bewertung_der_ausbildung_en.json'
            : '/Semesterreview_Content/semesterreview_bewertung_der_ausbildung.json',
        BB_LEISTUNGSBEURTEILUNG:
          language === 'en'
            ? '/Semesterreview_Content/semesterreview_leistungsbeurteilung_en.json'
            : '/Semesterreview_Content/semesterreview_leistungsbeurteilung.json',
      };

      const path = templatePaths[templateCode];
      if (!path) {
        throw new Error('Unknown template code: ' + templateCode);
      }

      const response = await fetch(path);
      const data = await response.json();

      // Pre-fill answers if they exist
      if (review && review.answers) {
        // The template is loaded, answers will be passed to the form
      }

      setTemplate(data);
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const handleSaveAnswers = async (answers) => {
    if (!isEditable) return; // Prevent saving if not editable

    try {
      setSaving(true);
      await manualSave();
      showToast('Answers saved successfully!');
      // Intentionally skipping loadReview() to preserve cursor focus and form state
    } catch (error) {
      console.error('Error saving answers:', error);
      showToast('Error while saving answers: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // Explicit, reversible action: copies the currently selected comment source into empty
  // fields only. Reuses the exact same `placeholders` map (itemCode -> comment) that already
  // drives the gray placeholder text, so prefilled values and placeholder hints can never
  // diverge. Never touches a field that already has a non-empty comment.
  const handleApplyComments = () => {
    if (!prefillEnabled || applyingComments || !review) return;

    const sourceKeys = Object.keys(placeholders);
    if (sourceKeys.length === 0) {
      showToast('No comments available to apply.', 'error');
      return;
    }

    setApplyingComments(true);
    try {
      const baseAnswers = currentAnswers || review.answers || {};
      const merged = { ...baseAnswers };
      let filledCount = 0;
      let skippedCount = 0;

      sourceKeys.forEach((itemCode) => {
        const existingComment = merged[itemCode]?.comment;
        if (existingComment && existingComment.trim() !== '') {
          skippedCount += 1;
          return;
        }
        merged[itemCode] = {
          scaleValue: merged[itemCode]?.scaleValue || '',
          comment: placeholders[itemCode],
        };
        filledCount += 1;
      });

      if (filledCount === 0) {
        showToast(
          skippedCount > 0
            ? `All ${skippedCount} matching fields already contain content - nothing was changed.`
            : 'No matching fields to fill.'
        );
        return;
      }

      // Feeds SemesterReviewForm's `initialAnswers` prop, which re-syncs its internal
      // form state and, through its existing onChange, updates currentAnswers/autosave too.
      setReview((prev) => ({ ...prev, answers: merged }));
      setCurrentAnswers(merged);

      showToast(
        skippedCount > 0
          ? `${filledCount} fields filled. ${skippedCount} existing fields were left unchanged.`
          : `${filledCount} fields filled.`
      );
    } finally {
      setApplyingComments(false);
    }
  };

  const validateReviewCompleteness = (currentAnswers) => {
    // Use answers from state or fallback to review.answers (if no changes made yet)
    const answersToCheck = currentAnswers || review.answers || {};

    // Special validation for Leistungsbeurteilung
    if (review.templateCode === 'BB_LEISTUNGSBEURTEILUNG') {
      // Required fields for Leistungsbeurteilung
      const requiredFields = [
        'firstName',
        'lastName',
        'year',
        'department',
        'trainerName',
        'overallRating',
        'pflichtPercent',
        'averagePercent',
        // Note: motivationPercent is optional/nullable in the wrapper logic
        'motivationText',
        'goalsNextSemester',
        'assessmentDate',
      ];

      for (const field of requiredFields) {
        const ans = answersToCheck[field];
        // Check scaleValue for rating/numbers/dates
        if (ans?.scaleValue && ans.scaleValue.trim() !== '') continue;
        // Check comment for text fields (motivationText etc store in comment)
        if (ans?.comment && ans.comment.trim() !== '') continue;

        return false; // Field missing
      }

      // Also check specific boolean/selection fields if they are critical
      // But the above Covers the main text/rating blocks.
      return true;
    }

    // Generic validation based on template items
    if (!template || !template.sections) return true;

    for (const section of template.sections) {
      if (!section.items) continue;
      for (const item of section.items) {
        const ans = answersToCheck[item.code];

        // Radio Scale Requirements
        if (item.scaleType === 'RADIO') {
          if (!ans || !ans.scaleValue) return false;
        }

        // Comment Requirements
        if (item.commentRequired) {
          if (!ans || !ans.comment || ans.comment.trim() === '') return false;
        }
      }
    }

    return true;
  };

  const handleStatusChange = async (newStatus) => {
    setPendingStatusChange(null);
    try {
      if (newStatus === 'IN_REVIEW' || newStatus === 'COMPLETED') {
        if (!isEditable) return;

        // Validate completeness before submitting
        if (!validateReviewCompleteness(currentAnswers)) {
          // Set message and show modal
          const msg =
            'Please ensure all required fields are filled out before submitting.\n\nRequired fields include:\n• Ratings (Self & Instructor)\n• Motivation & Goals\n';
          setValidationMessage(msg);
          setShowValidationModal(true);
          return;
        }

        // Flush pending edits before locking state transitions.
        if (isDirty) {
          await manualSave();
        }
      }

      setSaving(true);
      await semesterReviewService.updateStatus(id, newStatus);

      // If submitting (IN_REVIEW or COMPLETED) and user is trainer, redirect to trainer page
      const user = authService.getCurrentUser();
      const isTrainer =
        user &&
        user.roles?.some(
          (r) => r.name === 'BERUFSBILDNER' || r.name === 'PRAXISBILDNER' || r.name === 'ADMIN'
        );

      if ((newStatus === 'IN_REVIEW' || newStatus === 'COMPLETED') && isTrainer) {
        showToast('Review successfully submitted!');
        navigate('/trainer/semester-reviews');
      } else {
        showToast('Status updated!');
        await loadReview();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      showToast('Error updating status: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setSaving(true);
      semesterReviewService.revokeObjectUrl(pdfPreviewUrl);
      const { url, filename } = await semesterReviewService.getReviewPdfPreview(id);
      setPdfPreviewUrl(url);
      setPdfPreviewFilename(filename);
      setShowPdfPreview(true);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      showToast('Error exporting PDF: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const closePdfPreview = () => {
    setShowPdfPreview(false);
    semesterReviewService.revokeObjectUrl(pdfPreviewUrl);
    setPdfPreviewUrl('');
    setPdfPreviewFilename('Semesterreview.pdf');
  };

  const handlePdfDownload = () => {
    if (!pdfPreviewUrl) return;
    semesterReviewService.downloadPdfFromUrl(pdfPreviewUrl, pdfPreviewFilename);
    showToast('PDF download started!');
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      DRAFT: 'bg-gray-200 text-gray-800',
      IN_REVIEW: 'bg-blue-100 text-blue-800',
      COMPLETED: 'bg-green-100 text-green-800',
    };

    const statusLabels = {
      DRAFT: 'Draft',
      IN_REVIEW: 'In Review',
      COMPLETED: 'Completed',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusStyles[status]}`}>
        {statusLabels[status]}
      </span>
    );
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'TRAINER':
        return 'Vocational Trainer';
      case 'PRACTICE_INSTRUCTOR':
        return 'Practice Instructor';
      case 'LEARNER':
        return 'Me';
      case 'ADMIN':
        return 'Admin';
      default:
        return '';
    }
  };

  // Comment sources, grouped by who actually authored them - never assumed. A source only
  // counts as "Own comments" when its author's role matches the signed-in user's own role;
  // e.g. a Berufsbildner viewing a report a Practice Instructor wrote sees it labeled
  // "Practice Instructor comments", not lumped in with their own. `previousReviews` is
  // already scoped by the backend to reports the current user is authorized to see
  // (validateReviewAccess), so no extra frontend gating is needed here.
  const ROLE_GROUP_LABELS = {
    TRAINER: 'Vocational Trainer comments',
    PRACTICE_INSTRUCTOR: 'Practice Instructor comments',
    ADMIN: 'Admin comments',
    LEARNER: 'Learner comments',
  };
  const GROUP_ORDER = [
    'Own comments',
    'Vocational Trainer comments',
    'Practice Instructor comments',
    'Admin comments',
    'Learner comments',
  ];

  const groupLabelFor = (r) => {
    if (r.authorRole && r.authorRole !== 'LEARNER' && r.authorRole === currentUserRoleCategory) {
      return 'Own comments';
    }
    return ROLE_GROUP_LABELS[r.authorRole] || 'Other comments';
  };

  const groupedCommentSources = previousReviews.reduce((groups, r) => {
    const groupLabel = groupLabelFor(r);
    (groups[groupLabel] = groups[groupLabel] || []).push(r);
    return groups;
  }, {});

  const commentSourceGroupLabels = [
    ...GROUP_ORDER.filter((label) => groupedCommentSources[label]?.length),
    ...Object.keys(groupedCommentSources).filter((label) => !GROUP_ORDER.includes(label)),
  ];

  const formatCommentSourceLabel = (r) => {
    const dateStr = r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '';
    if (r.label && dateStr) return `${r.label} (${dateStr})`;
    if (r.label) return r.label;
    return dateStr ? `Report from ${dateStr}` : 'Report';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center">
        <div className="card-brand p-8">
          <i className="fas fa-spinner fa-spin text-3xl text-sunrise-red"></i>
          <p className="mt-4 text-grey-dusk">Loading Semester Review...</p>
        </div>
      </div>
    );
  }

  if (!review || !template) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center">
        <div className="card-brand p-8 text-center">
          <i className="fas fa-exclamation-triangle text-4xl text-sunrise-red mb-4"></i>
          <p className="text-grey-dusk">Review not found</p>
          <button
            onClick={() => navigate(-1)}
            className="btn-brand-primary inline-flex items-center gap-2 mt-2"
          >
            <i className="fas fa-arrow-left"></i>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-cloud p-6 relative">
      <LeaveProtectionModal
        blocker={blocker}
        onSaveAndLeave={async () => {
          if (!isEditable) return;

          if (currentAnswers || (review && review.answers)) {
            // Calculate total item count for bonus calc
            let totalCompetenceItems = 0;
            if (template && template.sections) {
              template.sections.forEach((s) => {
                if (s.items) totalCompetenceItems += s.items.length;
              });
            }
            try {
              await semesterReviewService.saveAnswers(
                id,
                currentAnswers || review.answers,
                totalCompetenceItems
              );
            } catch (error) {
              const backendMessage = error?.response?.data || error?.message || '';
              if (
                typeof backendMessage === 'string' &&
                backendMessage.includes('Cannot modify a completed review')
              ) {
                return;
              }
              throw error;
            }
          }
        }}
      />
      {/* Status Change Confirmation Modal */}
      {pendingStatusChange && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all scale-100 opacity-100">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 ${
                    pendingStatusChange === 'IN_REVIEW' ? 'bg-blue-100' : 'bg-green-100'
                  }`}
                >
                  <i
                    className={`fas text-lg ${
                      pendingStatusChange === 'IN_REVIEW'
                        ? 'fa-paper-plane text-blue-600'
                        : 'fa-lock text-green-600'
                    }`}
                  ></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">
                  {pendingStatusChange === 'IN_REVIEW'
                    ? isTrainerView
                      ? 'Send review to the apprentice?'
                      : 'Send review to your practice instructor?'
                    : 'Mark this review as completed?'}
                </h3>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 text-sm leading-relaxed">
                  {pendingStatusChange === 'IN_REVIEW'
                    ? isTrainerView
                      ? 'The apprentice will be able to see this review from now on. You can still edit it while it is in review.'
                      : 'Your practice instructor will be able to see this review from now on. You can still edit it while it is in review.'
                    : isTrainerView
                      ? 'The review will be finalized and locked. Neither you nor the apprentice will be able to edit it anymore — it can only be viewed and exported.'
                      : 'The review will be finalized and locked. Neither you nor your practice instructor will be able to edit it anymore — it can only be viewed and exported.'}
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setPendingStatusChange(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleStatusChange(pendingStatusChange)}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-all shadow-md hover:shadow-lg ${
                    pendingStatusChange === 'IN_REVIEW'
                      ? 'bg-sunrise-red hover:bg-sunrise-600 focus:ring-4 focus:ring-red-100'
                      : 'bg-green-500 hover:bg-green-600 focus:ring-4 focus:ring-green-100'
                  }`}
                  disabled={saving}
                >
                  {pendingStatusChange === 'IN_REVIEW' ? 'Send to review' : 'Complete review'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[70] p-4 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all scale-100 opacity-100">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 flex-shrink-0 bg-red-100">
                  <i className="fas fa-exclamation-triangle text-red-600 text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">Incomplete Form</h3>
              </div>

              <div className="mb-6 ml-13">
                <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">
                  {validationMessage}
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="px-4 py-2 text-sm font-medium text-white bg-sunrise-red hover:bg-sunrise-600 rounded-lg transition-all shadow-md hover:shadow-lg focus:ring-4 focus:ring-red-100"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-xl border-l-4 flex items-center animate-fade-in-down ${
            toast.type === 'error'
              ? 'bg-red-50 border-red-500 text-red-800'
              : 'bg-green-50 border-green-500 text-green-800'
          }`}
        >
          <i
            className={`fas ${toast.type === 'error' ? 'fa-exclamation-circle' : 'fa-check-circle'} mr-3 text-lg`}
          ></i>
          <div>
            <p className="font-medium">{toast.message}</p>
          </div>
          <button
            onClick={() => setToast({ ...toast, show: false })}
            className="ml-6 text-gray-400 hover:text-gray-600"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="card-brand p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-grey-dusk mb-2">{review.title}</h1>
              <p className="text-gray-600">
                Apprentice: <span className="font-medium">{review.learnerName}</span>
              </p>
              <p className="text-gray-600 flex items-center">
                Created by: <span className="font-medium ml-1">{review.authorName}</span>
                {review.authorRole &&
                  review.authorRole !== 'UNKNOWN' &&
                  review.authorRole !== 'LEARNER' && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                      {getRoleLabel(review.authorRole)}
                    </span>
                  )}
              </p>
            </div>
            <div className="text-right">
              {getStatusBadge(review.status)}
              <p className="text-sm text-gray-500 mt-2">
                Created: {new Date(review.createdAt).toLocaleDateString('de-CH')}
              </p>
              {review.completedAt && (
                <p className="text-sm text-gray-500">
                  Completed: {new Date(review.completedAt).toLocaleDateString('de-CH')}
                </p>
              )}

              {/* AutoSave Status */}
              {isEditable && (
                <div className="text-sm mt-2 flex justify-end items-center gap-2 transition-all min-h-[20px]">
                  {autoSaveStatus === 'saving' && (
                    <>
                      <i className="fas fa-spinner fa-spin text-sunrise-red text-xs"></i>
                      <span className="text-gray-500 italic">Saving...</span>
                    </>
                  )}
                  {autoSaveStatus === 'saved' && lastSavedTime && (
                    <>
                      <i className="fas fa-check text-green-500 text-xs"></i>
                      <span className="text-gray-400">
                        Saved at{' '}
                        {lastSavedTime.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </>
                  )}
                  {autoSaveStatus === 'modified' && (
                    <span className="text-orange-500 text-xs font-medium">Unsaved changes</span>
                  )}
                  {autoSaveStatus === 'error' && (
                    <span className="text-red-500 text-xs">Save failed</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Collapsible Workflow Status Diagram */}
          <div className="mt-4 border-t border-gray-200 pt-4">
            <button
              onClick={() => setShowWorkflow(!showWorkflow)}
              className="w-full flex items-center justify-between text-left px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm font-medium text-grey-dusk flex items-center">
                <i
                  className={`fas fa-chevron-${showWorkflow ? 'down' : 'right'} text-xs text-sunrise-red mr-2`}
                ></i>
                Review Workflow & Permissions
              </span>
              <span className="text-xs text-gray-500">
                Click to {showWorkflow ? 'hide' : 'show'}
              </span>
            </button>

            {showWorkflow && (
              <div className="mt-3 p-4 bg-pale-dawn rounded-xl border-2 border-sunrise-red/20">
                {/* Status Flow */}
                <div className="flex items-center justify-between mb-4">
                  {/* DRAFT */}
                  <div className="flex-1">
                    <div
                      className={`text-center p-2 rounded-lg border-2 transition-all ${
                        review.status === 'DRAFT'
                          ? 'bg-gray-100 border-grey-dusk shadow-md'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div
                        className={`text-sm font-bold ${review.status === 'DRAFT' ? 'text-grey-dusk' : 'text-gray-600'}`}
                      >
                        Draft
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">Editable</div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="px-2">
                    <i className="fas fa-arrow-right text-sunrise-red text-sm"></i>
                  </div>

                  {/* IN_REVIEW */}
                  <div className="flex-1">
                    <div
                      className={`text-center p-2 rounded-lg border-2 transition-all ${
                        review.status === 'IN_REVIEW'
                          ? 'bg-sunrise-red/10 border-sunrise-red shadow-md'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div
                        className={`text-sm font-bold ${review.status === 'IN_REVIEW' ? 'text-sunrise-red' : 'text-gray-600'}`}
                      >
                        In Review
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">Editable</div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="px-2">
                    <i className="fas fa-arrow-right text-sunrise-red text-sm"></i>
                  </div>

                  {/* COMPLETED */}
                  <div className="flex-1">
                    <div
                      className={`text-center p-2 rounded-lg border-2 transition-all ${
                        review.status === 'COMPLETED'
                          ? 'bg-green-100 border-green-500 shadow-md'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div
                        className={`text-sm font-bold ${review.status === 'COMPLETED' ? 'text-green-700' : 'text-gray-600'}`}
                      >
                        Completed
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">Read-only</div>
                    </div>
                  </div>
                </div>

                {/* Phase descriptions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                  <div className="p-2 bg-white/80 rounded border border-gray-200">
                    <div className="font-semibold text-grey-dusk mb-1 flex items-center">
                      <i className="fas fa-pencil-alt text-sunrise-red text-xs mr-1.5"></i>
                      Draft Phase
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Author prepares the review. Only visible to the author.
                    </p>
                  </div>
                  <div className="p-2 bg-white/80 rounded border border-gray-200">
                    <div className="font-semibold text-grey-dusk mb-1 flex items-center">
                      <i className="fas fa-users text-sunrise-red text-xs mr-1.5"></i>
                      In Review Phase
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Shared with learner. Author can still edit during discussions.
                    </p>
                  </div>
                  <div className="p-2 bg-white/80 rounded border border-gray-200">
                    <div className="font-semibold text-grey-dusk mb-1 flex items-center">
                      <i className="fas fa-lock text-sunrise-red text-xs mr-1.5"></i>
                      Completed Phase
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Finalized and locked. Both parties can view and export.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Action buttons for author */}
          {isEditable && (
            <div className="pt-4 border-t border-gray-200 mt-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                {/* Back and Export (left side) */}
                <div className="flex gap-2">
                  <button
                    onClick={() => navigate(-1)}
                    className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 
                     hover:bg-gray-50 text-sm font-medium flex items-center gap-1.5 transition-colors"
                  >
                    <i className="fas fa-arrow-left text-xs"></i>
                    <span>Back</span>
                  </button>
                  <button
                    onClick={handleExportPdf}
                    className="px-3 py-2 rounded-lg border border-sunrise-red bg-white text-sunrise-red 
                     hover:bg-red-50 text-sm font-medium flex items-center gap-1.5 transition-colors"
                    disabled={saving}
                  >
                    <i className="fas fa-file-pdf text-xs"></i>
                    <span>Export PDF</span>
                  </button>
                </div>

                {/* Main actions (right side) */}
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                  {/* Save draft (secondary) */}
                  <button
                    onClick={() => handleSaveAnswers(currentAnswers || review.answers)}
                    className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 
                     text-gray-800 transition-colors text-sm font-medium flex items-center justify-center gap-1.5"
                    disabled={saving}
                  >
                    <i className="fas fa-save text-xs"></i>
                    <span>{saving ? 'Saving...' : 'Save draft'}</span>
                  </button>

                  {/* Primary action depending on status */}
                  {review.status === 'DRAFT' && (
                    <button
                      onClick={() => setPendingStatusChange('IN_REVIEW')}
                      className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-sunrise-red hover:bg-sunrise-600 
                       text-white transition-colors text-sm font-semibold flex items-center justify-center gap-1.5"
                      disabled={saving}
                    >
                      <i className="fas fa-paper-plane text-xs"></i>
                      <span>Submit</span>
                    </button>
                  )}

                  {review.status === 'IN_REVIEW' && (
                    <button
                      onClick={() => setPendingStatusChange('COMPLETED')}
                      className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-green-500 hover:bg-green-600 
                       text-white transition-colors text-sm font-semibold flex items-center justify-center gap-1.5"
                      disabled={saving}
                    >
                      <i className="fas fa-check text-xs"></i>
                      <span>Mark as completed</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {!isEditable && (
            <div className="pt-4 border-t border-gray-200">
              <div className="flex gap-2">
                <button
                  onClick={() => navigate(-1)}
                  className="px-3 py-2 rounded-lg bg-sunrise-red hover:bg-sunrise-600 text-white 
                   transition-colors text-sm font-semibold flex items-center gap-1.5"
                >
                  <i className="fas fa-arrow-left text-xs"></i>
                  <span>Back</span>
                </button>
                <button
                  onClick={handleExportPdf}
                  className="px-3 py-2 rounded-lg border-2 border-sunrise-red bg-white text-sunrise-red 
                   hover:bg-red-50 transition-colors text-sm font-semibold flex items-center gap-1.5"
                  disabled={saving}
                >
                  <i className="fas fa-file-pdf text-xs"></i>
                  <span>Export PDF</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Placeholder / Comment Source Selector */}
        {isEditable && review.templateCode !== 'BB_LEISTUNGSBEURTEILUNG' && (
          <div className="mb-6 card-brand-elevated p-4 bg-gradient-to-r from-white to-blue-50/50 border border-blue-100">
            <div className="flex flex-col md:flex-row md:items-start gap-4">
              <div className="flex-grow max-w-md">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  <i className="fas fa-history mr-1.5 text-sunrise-red"></i>
                  Previous comments
                </label>
                <div className="relative">
                  <select
                    value={selectedPlaceholderSource}
                    onChange={(e) => setSelectedPlaceholderSource(e.target.value)}
                    className="appearance-none bg-white block w-full pl-3 pr-10 py-2.5 text-sm border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red rounded-lg transition-shadow"
                    disabled={previousReviews.length === 0}
                    aria-label="Comment source"
                  >
                    <option value="">
                      {previousReviews.length === 0
                        ? 'No previous report available'
                        : 'Empty (no placeholders)'}
                    </option>

                    {/* Grouped by who actually authored the comments - never assumed to be
                        "own" just because the viewer is staff. */}
                    {commentSourceGroupLabels.map((groupLabel) => (
                      <optgroup key={groupLabel} label={groupLabel}>
                        {groupedCommentSources[groupLabel].map((r) => (
                          <option key={r.id} value={r.id}>
                            {formatCommentSourceLabel(r)} – {groupLabel}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <i className="fas fa-chevron-down text-xs"></i>
                  </div>
                </div>

                {/* Optional, explicit prefill - kept visually secondary to the dropdown.
                    Nothing to prefill from when there is no previous report at all. */}
                {previousReviews.length > 0 && (
                <div className="mt-3 pt-3 border-t border-blue-100/70">
                  <label className="flex items-start gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={prefillEnabled}
                      onChange={(e) => setPrefillEnabled(e.target.checked)}
                      className="mt-0.5 w-4 h-4 accent-sunrise-red rounded"
                      aria-describedby="prefill-fields-description"
                    />
                    <span className="text-sm font-medium text-grey-dusk">
                      Prefill fields with selected comments
                    </span>
                  </label>
                  <p id="prefill-fields-description" className="text-xs text-gray-500 mt-1 ml-6">
                    Copies the selected comments into empty fields. The inserted text can then
                    be edited normally.
                  </p>

                  {prefillEnabled && (
                    <button
                      type="button"
                      onClick={handleApplyComments}
                      disabled={
                        applyingComments ||
                        !selectedPlaceholderSource ||
                        Object.keys(placeholders).length === 0
                      }
                      className="mt-3 ml-6 px-4 py-2 rounded-lg bg-sunrise-red hover:bg-sunrise-600 text-white text-sm font-semibold flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {applyingComments ? (
                        <i className="fas fa-circle-notch fa-spin text-xs"></i>
                      ) : (
                        <i className="fas fa-file-import text-xs"></i>
                      )}
                      Apply comments
                    </button>
                  )}
                </div>
                )}
              </div>
              <div className="text-gray-600 text-sm md:pt-6 flex items-center">
                <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-lightbulb text-xs"></i>
                </div>
                <span>
                  Select a source to display its comments as
                  <span className="font-semibold text-gray-800 mx-1">light gray placeholders</span>
                  in empty fields.
                </span>
              </div>
            </div>
          </div>
        )}
        {/* Quick Notes — read-only section for trainers and admins */}
        {canViewNotes && (
          <div className="mt-6 bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center gap-2">
              <i className="fas fa-sticky-note text-sunrise-red text-xs"></i>
              <h3 className="text-sm font-bold text-grey-dusk">Quick Notes</h3>
              {reviewNotes.length > 0 && reviewNotes[0].semesterName ? (
                <span className="text-xs text-gray-400 font-normal">
                  · {reviewNotes[0].semesterName}
                </span>
              ) : review.semesterLabel ? (
                <span className="text-xs text-gray-400 font-normal">· {review.semesterLabel}</span>
              ) : null}
            </div>

            <div className="px-5 py-4">
              {reviewNotesLoading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : reviewNotesError ? (
                <p className="text-sm text-red-500 flex items-center gap-1.5">
                  <i className="fas fa-exclamation-circle text-xs"></i>
                  {reviewNotesError}
                </p>
              ) : reviewNotes.length === 0 ? (
                <p className="text-sm text-gray-400 italic">
                  No quick notes for this semester yet.
                </p>
              ) : (
                <div className="space-y-2">
                  {reviewNotes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-pale-dawn/40 border border-gray-200 rounded-xl px-4 py-3"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-sunrise-red">
                          {note.noteDate
                            ? new Date(note.noteDate).toLocaleDateString('de-CH', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : ''}
                        </span>
                        {note.authorName && (
                          <span className="text-xs text-gray-400">· {note.authorName}</span>
                        )}
                      </div>
                      <p className="text-sm text-grey-dusk leading-relaxed whitespace-pre-wrap">
                        {note.text}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {/* Smart Goal Placeholder Source Selector (Leistungsbeurteilung) */}
        {isEditable && review.templateCode === 'BB_LEISTUNGSBEURTEILUNG' && (
          <div className="mt-6 mb-6 card-brand-elevated p-4 bg-gradient-to-r from-white to-red-50/50 border border-red-100">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-grow max-w-md">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  <i className="fas fa-history mr-1.5 text-sunrise-red"></i>
                  Pre-filled placeholders (from previous reviews)
                </label>
                <div className="relative">
                  <select
                    value={selectedMeetingSource}
                    onChange={(e) => setSelectedMeetingSource(e.target.value)}
                    className="appearance-none bg-white block w-full pl-3 pr-10 py-2.5 text-sm border border-gray-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-sunrise-red focus:border-sunrise-red rounded-lg transition-shadow"
                    disabled={previousMeetings.length === 0}
                  >
                    <option value="">
                      {previousMeetings.length === 0 ? 'No previous meetings' : 'Select...'}
                    </option>
                    {previousMeetings.map((r) => (
                      <option key={r.id} value={String(r.id)}>
                        {r.label
                          ? `${r.label} (${new Date(r.createdAt).toLocaleDateString()})`
                          : `Meeting ${new Date(r.createdAt).toLocaleDateString()}`}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <i className="fas fa-chevron-down text-xs"></i>
                  </div>
                </div>
              </div>
              <div className="text-gray-600 text-sm md:pt-6 flex items-center">
                <div className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-lightbulb text-xs"></i>
                </div>
                <span>
                  Select a report to display its comments as
                  <span className="font-semibold text-gray-800 mx-1">light gray placeholders</span>
                  in empty fields.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        {review.templateCode === 'BB_LEISTUNGSBEURTEILUNG' ? (
          <LeistungsbeurteilungFormWrapper
            review={review}
            onSave={handleSaveAnswers}
            onAnswersChange={setCurrentAnswers}
            readOnly={!isEditable}
            language={language}
            smartGoalPlaceholder={smartGoalPlaceholder}
            previousAnswers={review.previousAnswers || {}}
          />
        ) : (
          <SemesterReviewForm
            template={template}
            initialAnswers={review.answers}
            onSubmit={handleSaveAnswers}
            onChange={setCurrentAnswers}
            readOnly={!isEditable}
            language={language}
            showBonusCalculation={review.templateCode === 'KOMPETENZEN'}
            bonusStats={{
              totalPoints: review.totalPoints || 0,
              maxPoints: review.maxPoints || 0,
              percentage: review.bonusPercentage || 0,
            }}
            placeholders={placeholders}
          />
        )}
      </div>

      {showPdfPreview && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-6xl h-[88vh] bg-white rounded-2xl shadow-brand-xl border border-pale-dawn flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-base font-semibold text-grey-dusk flex items-center gap-2">
                  <i className="fas fa-file-pdf text-sunrise-red"></i>
                  <span>PDF Preview</span>
                </div>
                <div className="text-sm text-gray-500 truncate">{pdfPreviewFilename}</div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={handlePdfDownload}
                  className="px-4 py-2 rounded-lg bg-sunrise-red hover:bg-sunrise-600 text-white text-sm font-semibold flex items-center gap-2 transition-colors"
                >
                  <i className="fas fa-download text-xs"></i>
                  <span>Download PDF</span>
                </button>
                <button
                  onClick={closePdfPreview}
                  className="w-10 h-10 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors flex items-center justify-center"
                  aria-label="Close PDF preview"
                >
                  <i className="fas fa-times text-sm"></i>
                </button>
              </div>
            </div>

            <div className="flex-1 bg-gray-100 p-4">
              {pdfPreviewUrl ? (
                <iframe
                  title="Semester review PDF preview"
                  src={pdfPreviewUrl}
                  className="w-full h-full rounded-xl border border-gray-200 bg-white"
                />
              ) : (
                <div className="w-full h-full rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-500">
                  Loading preview...
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper component to convert between answer format and form values
const LeistungsbeurteilungFormWrapper = ({
  review,
  onSave,
  onAnswersChange,
  readOnly,
  language,
  smartGoalPlaceholder,
  previousAnswers,
}) => {
  // Extract learner first and last name from review.learnerName
  const extractLearnerNames = (learnerName) => {
    if (!learnerName) return { firstName: '', lastName: '' };
    const parts = learnerName.trim().split(' ');
    if (parts.length === 1) {
      return { firstName: parts[0], lastName: '' };
    }
    // First part is firstName, rest is lastName
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };

  const { firstName: learnerFirstName, lastName: learnerLastName } = extractLearnerNames(
    review.learnerName
  );
  const [errors] = useState({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationMessages] = useState([]);

  // Convert answers to LeistungsbeurteilungValues format
  const answersToValues = (answers) => {
    // If assessmentDate is missing in answers, use today's date
    const today = new Date().toISOString().split('T')[0];
    const assessmentDate = answers?.assessmentDate?.scaleValue || today;

    const values = {
      firstName: answers?.firstName?.scaleValue || learnerFirstName,
      lastName: answers?.lastName?.scaleValue || learnerLastName,
      year: answers?.year?.scaleValue || (review.learnerYear ? String(review.learnerYear) : ''),
      department: answers?.department?.scaleValue || '',
      overallRating: answers?.overallRating?.scaleValue || '',
      pflichtPercent: 0,
      motivationPercent: answers?.motivationPercent?.scaleValue
        ? Number(answers.motivationPercent.scaleValue)
        : null,
      averagePercent: 0,
      pflicht_notenDatenbank: answers?.pflicht_notenDatenbank?.scaleValue || '',
      pflicht_notenDatenbankComment: answers?.pflicht_notenDatenbank?.comment || '',
      pflicht_zeiterfassung: answers?.pflicht_zeiterfassung?.scaleValue || '',
      pflicht_zeiterfassungComment: answers?.pflicht_zeiterfassung?.comment || '',
      pflicht_absenzen: answers?.pflicht_absenzen?.scaleValue || '',
      pflicht_absenzenComment: answers?.pflicht_absenzen?.comment || '',
      pflicht_protimeAbsenzen: answers?.pflicht_protimeAbsenzen?.scaleValue || '',
      pflicht_protimeAbsenzenComment: answers?.pflicht_protimeAbsenzen?.comment || '',
      pflicht_ausbildungskontrolle: answers?.pflicht_ausbildungskontrolle?.scaleValue || '',
      pflicht_ausbildungskontrolleComment: answers?.pflicht_ausbildungskontrolle?.comment || '',
      pflicht_auftraege: answers?.pflicht_auftraege?.scaleValue || '',
      pflicht_auftraegeComment: answers?.pflicht_auftraege?.comment || '',
      pflicht_outlook: answers?.pflicht_outlook?.scaleValue || '',
      pflicht_outlookComment: answers?.pflicht_outlook?.comment || '',
      pflicht_abmachungen: answers?.pflicht_abmachungen?.scaleValue || '',
      pflicht_abmachungenComment: answers?.pflicht_abmachungen?.comment || '',
      pflicht_verhaltenSchule: answers?.pflicht_verhaltenSchule?.scaleValue || '',
      pflicht_verhaltenSchuleComment: answers?.pflicht_verhaltenSchule?.comment || '',
      motivationText: answers?.motivationText?.comment || '',
      goalsNextSemester: answers?.goalsNextSemester?.comment || '',
      remarks: answers?.remarks?.comment || '',
      assessmentDate: assessmentDate,
      trainerName: answers?.trainerName?.scaleValue || review.authorName || '',
    };
    return values;
  };

  // Convert LeistungsbeurteilungValues back to answers format
  const valuesToAnswers = (values) => {
    const answers = {};

    // Header fields
    answers.firstName = { scaleValue: values.firstName, comment: '' };
    answers.lastName = { scaleValue: values.lastName, comment: '' };
    answers.year = { scaleValue: values.year, comment: '' };
    answers.department = { scaleValue: values.department, comment: '' };

    // Rating
    answers.overallRating = { scaleValue: values.overallRating, comment: '' };
    answers.pflichtPercent = {
      scaleValue: String(values.pflichtPercent),
      comment: '',
    };
    answers.motivationPercent = {
      scaleValue: values.motivationPercent !== null ? String(values.motivationPercent) : '',
      comment: '',
    };
    answers.averagePercent = {
      scaleValue: String(values.averagePercent),
      comment: '',
    };

    // Pflicht items
    answers.pflicht_notenDatenbank = {
      scaleValue: values.pflicht_notenDatenbank,
      comment: values.pflicht_notenDatenbankComment || '',
    };
    answers.pflicht_zeiterfassung = {
      scaleValue: values.pflicht_zeiterfassung,
      comment: values.pflicht_zeiterfassungComment || '',
    };
    answers.pflicht_absenzen = {
      scaleValue: values.pflicht_absenzen,
      comment: values.pflicht_absenzenComment || '',
    };
    answers.pflicht_protimeAbsenzen = {
      scaleValue: values.pflicht_protimeAbsenzen,
      comment: values.pflicht_protimeAbsenzenComment || '',
    };
    answers.pflicht_ausbildungskontrolle = {
      scaleValue: values.pflicht_ausbildungskontrolle,
      comment: values.pflicht_ausbildungskontrolleComment || '',
    };
    answers.pflicht_auftraege = {
      scaleValue: values.pflicht_auftraege,
      comment: values.pflicht_auftraegeComment || '',
    };
    answers.pflicht_outlook = {
      scaleValue: values.pflicht_outlook,
      comment: values.pflicht_outlookComment || '',
    };
    answers.pflicht_abmachungen = {
      scaleValue: values.pflicht_abmachungen,
      comment: values.pflicht_abmachungenComment || '',
    };
    answers.pflicht_verhaltenSchule = {
      scaleValue: values.pflicht_verhaltenSchule,
      comment: values.pflicht_verhaltenSchuleComment || '',
    };

    // Text fields
    answers.motivationText = { scaleValue: '', comment: values.motivationText };
    answers.goalsNextSemester = {
      scaleValue: '',
      comment: values.goalsNextSemester,
    };
    answers.remarks = { scaleValue: '', comment: values.remarks };

    // Signatures
    answers.assessmentDate = { scaleValue: values.assessmentDate, comment: '' };
    answers.trainerName = { scaleValue: values.trainerName, comment: '' };

    return answers;
  };

  const [formValues, setFormValues] = useState(() => answersToValues(review.answers));

  // Update formValues.year from logic if needed, but it might be set by user or backend.
  // The requirement says "add a read-only field... update automatically... match stored user.lehrjahr".
  // If we have access to learner data, we can set it.
  // review.learner object might be available if we fetched it. But review only contains ID and Name usually in simple DTO.
  // However, loadReview in parent sets 'review' state which comes from semesterReviewService.getReviewById.
  // This likely includes full SemesterReviewDetailResponse which has learner details.
  // But wait, SemesterReviewDetailResponse usually has `learnerName`, `learnerId`.
  // We might not have `lehrjahr` directly there unless we fetch it.
  // Assuming the user's lehrjahr is correct in the backend, we should prefill it.
  // I'll rely on what's passed or try to use what's available.

  const handleChange = (newValues) => {
    setFormValues(newValues);
    const answers = valuesToAnswers(newValues);
    if (onAnswersChange) {
      onAnswersChange(answers);
    }
  };

  const handleSave = () => {
    const answers = valuesToAnswers(formValues);
    onSave(answers);
  };

  return (
    <div className="card-brand p-6">
      {/* Validation Modal */}
      {showValidationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl border border-pale-dawn p-6 max-w-md w-full shadow-brand-xl">
            <h3 className="text-lg font-bold text-red-600 mb-4">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              Failed to save - validation errors
            </h3>
            <p className="mb-4 text-gray-700">Please correct the following validation errors:</p>
            <ul className="list-disc pl-5 mb-6 text-sm text-gray-600 space-y-1">
              {validationMessages.map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowValidationModal(false);
                  // Optional: Scroll to first error
                  const firstErrorField = document.querySelector('.border-red-500');
                  if (firstErrorField)
                    firstErrorField.scrollIntoView({
                      behavior: 'smooth',
                      block: 'center',
                    });
                }}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-800 text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <LeistungsbeurteilungForm
        value={formValues}
        onChange={handleChange}
        readOnly={readOnly}
        learnerFirstName={learnerFirstName}
        learnerLastName={learnerLastName}
        language={language}
        errors={errors}
        smartGoalPlaceholder={smartGoalPlaceholder}
        previousAnswers={previousAnswers}
        yearPlaceholder={review.learnerYear ? String(review.learnerYear) : ''}
      />

      {!readOnly && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            className="w-full inline-flex items-center justify-center px-6 py-3 rounded-lg bg-sunrise-red text-white font-semibold text-sm md:text-base hover:bg-sunrise-600 transition-colors shadow-brand focus:outline-none focus:ring-2 focus:ring-sunrise-red/60 focus:ring-offset-2"
          >
            <i className="fas fa-save mr-2"></i>
            Save Form
          </button>
        </div>
      )}
    </div>
  );
};

export default SemesterReviewDetailPage;
